import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { getDynamicTagParams } from "@/services/tagsApi";
import type { DynamicTagParamDTO } from "@/types/api";

export type TargetType = "DEVICE" | "USER";
export type BackendOperator =
  | "EQ" | "NEQ" | "CONTAINS" | "NOT_CONTAINS"
  | "STARTS_WITH" | "ENDS_WITH"
  | "IN" | "NOT_IN"
  | "GT" | "LT" | "GTE" | "LTE";

export interface RuleCondition {
  rule_join_operator: "AND" | "OR" | null;
  target_type: TargetType;
  param: string;
  operator: BackendOperator | "";
  value: string;
  data_type: string;
}

const OPERATOR_LABELS: Record<string, string> = {
  EQ: "Equals",
  NEQ: "Not Equals",
  CONTAINS: "Contains",
  NOT_CONTAINS: "Does not contain",
  STARTS_WITH: "Starts with",
  ENDS_WITH: "Ends with",
  IN: "Is one of",
  NOT_IN: "Is none of",
  GT: "Greater than",
  LT: "Less than",
  GTE: "Greater than or equal",
  LTE: "Less than or equal",
};

function prettifyParam(name: string): string {
  return name
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

interface ConditionGroupProps {
  groupIndex: number;
  conditions: RuleCondition[];
  onConditionsChange: (conditions: RuleCondition[]) => void;
  onRemoveGroup: () => void;
  canRemoveGroup: boolean;
}

export function ConditionGroup({
  groupIndex,
  conditions,
  onConditionsChange,
  onRemoveGroup,
  canRemoveGroup,
}: ConditionGroupProps) {
  // Fetch param schema for both target types (cached)
  const deviceParams = useQuery({
    queryKey: ["tag-params", "DEVICE"],
    queryFn: () => getDynamicTagParams("DEVICE"),
    staleTime: 5 * 60 * 1000,
  });
  const userParams = useQuery({
    queryKey: ["tag-params", "USER"],
    queryFn: () => getDynamicTagParams("USER"),
    staleTime: 5 * 60 * 1000,
  });

  const paramsFor = (target: TargetType): DynamicTagParamDTO[] => {
    return (target === "DEVICE" ? deviceParams.data : userParams.data) || [];
  };

  const addCondition = () => {
    onConditionsChange([
      ...conditions,
      {
        rule_join_operator: conditions.length === 0 ? null : "AND",
        target_type: "DEVICE",
        param: "",
        operator: "",
        value: "",
        data_type: "TEXT",
      },
    ]);
  };

  const removeCondition = (index: number) => {
    if (conditions.length === 1) return;
    const next = conditions.filter((_, i) => i !== index);
    // First condition's join operator is always null
    if (next[0]) next[0] = { ...next[0], rule_join_operator: null };
    onConditionsChange(next);
  };

  const updateCondition = (index: number, patch: Partial<RuleCondition>) => {
    const next = [...conditions];
    next[index] = { ...next[index], ...patch };
    onConditionsChange(next);
  };

  const handleTargetChange = (index: number, target: TargetType) => {
    updateCondition(index, {
      target_type: target,
      param: "",
      operator: "",
      value: "",
      data_type: "TEXT",
    });
  };

  const handleParamChange = (index: number, paramName: string) => {
    const c = conditions[index];
    const def = paramsFor(c.target_type).find((p) => p.fieldName === paramName);
    const supported = def?.supportedOperators || [];
    updateCondition(index, {
      param: paramName,
      data_type: def?.dataType || "TEXT",
      operator: (supported.includes(c.operator as string) ? c.operator : (supported[0] as BackendOperator)) || "",
      value: "",
    });
  };

  const handleJoinChange = (index: number, op: "AND" | "OR") => {
    updateCondition(index, { rule_join_operator: op });
  };

  return (
    <div className="border border-border rounded-lg p-4 bg-card/50 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-primary tracking-wide">
          GROUP {groupIndex + 1}
        </span>
        {canRemoveGroup && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemoveGroup}
            className="h-8 w-8 p-0"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {conditions.map((condition, index) => {
          const def = paramsFor(condition.target_type).find(
            (p) => p.fieldName === condition.param
          );
          const supported = def?.supportedOperators || [];

          return (
            <div key={index} className="space-y-2">
              {index > 0 && (
                <div className="flex items-center justify-center">
                  <Select
                    value={condition.rule_join_operator || "AND"}
                    onValueChange={(v: "AND" | "OR") => handleJoinChange(index, v)}
                  >
                    <SelectTrigger className="w-20 h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AND">AND</SelectItem>
                      <SelectItem value="OR">OR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex items-center gap-2">
                {/* Target type */}
                <Select
                  value={condition.target_type}
                  onValueChange={(v: TargetType) => handleTargetChange(index, v)}
                >
                  <SelectTrigger className="w-28 bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DEVICE">Device</SelectItem>
                    <SelectItem value="USER">User</SelectItem>
                  </SelectContent>
                </Select>

                {/* Param */}
                <Select
                  value={condition.param}
                  onValueChange={(v) => handleParamChange(index, v)}
                >
                  <SelectTrigger className="flex-1 bg-background">
                    <SelectValue placeholder="Choose property" />
                  </SelectTrigger>
                  <SelectContent>
                    {paramsFor(condition.target_type).map((p) => (
                      <SelectItem key={p.fieldName} value={p.fieldName}>
                        {prettifyParam(p.fieldName)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Operator */}
                <Select
                  value={condition.operator}
                  onValueChange={(v: BackendOperator) =>
                    updateCondition(index, { operator: v, value: "" })
                  }
                  disabled={!condition.param}
                >
                  <SelectTrigger className="w-40 bg-background">
                    <SelectValue placeholder="Operator" />
                  </SelectTrigger>
                  <SelectContent>
                    {supported.map((op) => (
                      <SelectItem key={op} value={op}>
                        {OPERATOR_LABELS[op] || op}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Value */}
                {condition.data_type === "BOOLEAN" ? (
                  <Select
                    value={condition.value}
                    onValueChange={(v) => updateCondition(index, { value: v })}
                  >
                    <SelectTrigger className="flex-1 bg-background">
                      <SelectValue placeholder="Value" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Yes</SelectItem>
                      <SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                ) : condition.data_type === "ENUM" && def?.allowedValues?.length ? (
                  <Select
                    value={condition.value}
                    onValueChange={(v) => updateCondition(index, { value: v })}
                  >
                    <SelectTrigger className="flex-1 bg-background">
                      <SelectValue placeholder="Choose value" />
                    </SelectTrigger>
                    <SelectContent>
                      {def.allowedValues.map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    className="flex-1 bg-background"
                    type={condition.data_type === "TIMESTAMP" ? "datetime-local" : "text"}
                    placeholder={
                      condition.operator === "IN" || condition.operator === "NOT_IN"
                        ? "value1,value2"
                        : "value"
                    }
                    value={condition.value}
                    onChange={(e) => updateCondition(index, { value: e.target.value })}
                  />
                )}

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeCondition(index)}
                  disabled={conditions.length === 1}
                  className="shrink-0 h-8 w-8 p-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={addCondition}
        className="w-full h-8"
        size="sm"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Rule to Group {groupIndex + 1}
      </Button>
    </div>
  );
}
